import React, { useState, useCallback, useRef } from 'react';
import { apiFetch } from '../api';
import VideoPlayer, { ToCanvasFn } from './VideoPlayer';

interface Point { x: number; y: number; }

interface OverlayData {
    frame: number;
    startFrame: number;
    endFrame: number;
    h_lines: Point[][];
    v_lines: Point[][];
    boundary: Point[];
}

interface SegmentsResponse {
    video_id: number;
    isPanorama?: boolean;
    fps: number;
    duration: number;
    video_url: string;
    count: number;
}

const CHUNK_SIZE = 300;

const HomographyViewer: React.FC = () => {
    const [scheduleId, setScheduleId] = useState('');
    const [segmentsData, setSegmentsData] = useState<SegmentsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentFrame, setCurrentFrame] = useState(0);
    const [infoText, setInfoText] = useState('');
    const [cacheInfo, setCacheInfo] = useState('');

    const overlayCache = useRef<OverlayData[]>([]);
    const currentFrameRef = useRef(0);
    const scheduleIdRef = useRef('');
    const requestedChunks = useRef<Set<number>>(new Set());

    const findCachedOverlay = (frame: number): OverlayData | null => {
        const cache = overlayCache.current;
        if (cache.length === 0) return null;
        let lo = 0, hi = cache.length - 1;
        while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (cache[mid].frame < frame) lo = mid + 1;
            else hi = mid;
        }
        // lo is the first entry with frame >= target; pick nearest between lo and lo-1
        let best = lo;
        if (lo > 0) {
            const dLo = Math.abs(cache[lo].frame - frame);
            const dPrev = Math.abs(cache[lo - 1].frame - frame);
            if (dPrev < dLo) best = lo - 1;
        }
        return cache[best];
    };

    const insertBulk = (entries: OverlayData[]) => {
        const cache = overlayCache.current;
        for (const entry of entries) {
            let lo = 0, hi = cache.length;
            while (lo < hi) {
                const mid = (lo + hi) >> 1;
                if (cache[mid].frame < entry.frame) lo = mid + 1;
                else hi = mid;
            }
            if (lo < cache.length && cache[lo].frame === entry.frame) {
                cache[lo] = entry;
            } else {
                cache.splice(lo, 0, entry);
            }
        }
    };

    const ensureChunk = (chunkIdx: number) => {
        if (chunkIdx < 0 || requestedChunks.current.has(chunkIdx)) return;
        const id = parseInt(scheduleIdRef.current, 10);
        if (isNaN(id)) return;
        requestedChunks.current.add(chunkIdx);

        const startFrame = chunkIdx * CHUNK_SIZE;
        const endFrame = startFrame + CHUNK_SIZE;
        apiFetch(
            `/train/homography_overlays_bulk?schedule_id=${id}&start_frame=${startFrame}&end_frame=${endFrame}&grid_size=6&step=1`
        ).then(response => {
            if (!response.ok) return;
            return response.json();
        }).then(data => {
            if (data?.overlays?.length) {
                insertBulk(data.overlays);
            }
        }).catch(() => {
            requestedChunks.current.delete(chunkIdx);
        });
    };

    const handleFrameChange = useCallback((frame: number) => {
        currentFrameRef.current = frame;
        setCurrentFrame(frame);

        const cache = overlayCache.current;
        const cached = findCachedOverlay(frame);
        const hasExact = cached !== null && cached.frame === frame;
        if (cached) {
            setInfoText(`Segment: ${cached.startFrame}-${cached.endFrame}${hasExact ? '' : ' (loading...)'}`);
        } else {
            setInfoText('(no data)');
        }
        if (cache.length > 0) {
            setCacheInfo(`Cached: ${cache[0].frame}-${cache[cache.length - 1].frame} (${cache.length})`);
        }

        const idx = Math.floor(frame / CHUNK_SIZE);
        ensureChunk(idx);
        ensureChunk(idx + 1);
    }, []);

    const drawOverlay = useCallback((ctx: CanvasRenderingContext2D, toCanvas: ToCanvasFn) => {
        const data = findCachedOverlay(currentFrameRef.current);
        if (!data || data.frame !== currentFrameRef.current) return;

        if (data.boundary.length > 2) {
            ctx.beginPath();
            const [bx0, by0] = toCanvas(data.boundary[0].x, data.boundary[0].y);
            ctx.moveTo(bx0, by0);
            for (let i = 1; i < data.boundary.length; i++) {
                const [bx, by] = toCanvas(data.boundary[i].x, data.boundary[i].y);
                ctx.lineTo(bx, by);
            }
            ctx.closePath();
            ctx.fillStyle = 'rgba(200, 100, 80, 0.3)';
            ctx.fill();
        }

        const drawPolyline = (points: Point[], color: string, width: number) => {
            if (points.length < 2) return;
            ctx.beginPath();
            const [sx, sy] = toCanvas(points[0].x, points[0].y);
            ctx.moveTo(sx, sy);
            for (let i = 1; i < points.length; i++) {
                const [px, py] = toCanvas(points[i].x, points[i].y);
                ctx.lineTo(px, py);
            }
            ctx.strokeStyle = color;
            ctx.lineWidth = width;
            ctx.stroke();
        };

        for (const line of data.h_lines) drawPolyline(line, 'rgba(100, 200, 100, 0.6)', 1.5);
        for (const line of data.v_lines) drawPolyline(line, 'rgba(100, 200, 100, 0.6)', 1.5);
    }, []);

    const loadSchedule = async () => {
        const id = parseInt(scheduleId, 10);
        if (isNaN(id)) return;

        setLoading(true);
        setError(null);
        setInfoText('');
        setCacheInfo('');
        overlayCache.current = [];
        requestedChunks.current = new Set();
        scheduleIdRef.current = scheduleId;

        try {
            const response = await apiFetch(`/train/homography_segments?schedule_id=${id}`);
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.detail || `HTTP ${response.status}`);
            }
            const result: SegmentsResponse = await response.json();
            setSegmentsData(result);

            ensureChunk(0);
            ensureChunk(1);
        } catch (err: any) {
            setError(err.message);
            setSegmentsData(null);
        } finally {
            setLoading(false);
        }
    };

    const totalSegments = segmentsData?.count ?? 0;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
                <input
                    type="text"
                    placeholder="Schedule ID"
                    value={scheduleId}
                    onChange={(e) => setScheduleId(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && loadSchedule()}
                    style={{ padding: '8px', width: '150px' }}
                />
                <button
                    onClick={loadSchedule}
                    disabled={loading}
                    style={{ padding: '8px 16px' }}
                >
                    {loading ? 'Loading...' : 'Load'}
                </button>

                {segmentsData && (
                    <span style={{ color: '#666', fontSize: '13px' }}>
                        Video {segmentsData.video_id} |
                        {' '}{segmentsData.isPanorama === false ? 'Non-panorama' : 'Panorama'} |
                        {' '}{segmentsData.fps} fps |
                        {' '}{totalSegments} segments |
                        {' '}Frame: {currentFrame}
                        {infoText && ` | ${infoText}`}
                        {cacheInfo && ` | ${cacheInfo}`}
                    </span>
                )}
            </div>

            {error && <div style={{ color: 'red', marginBottom: '10px' }}>Error: {error}</div>}

            {segmentsData && (
                <VideoPlayer
                    videoUrl={segmentsData.video_url}
                    fps={segmentsData.fps}
                    onFrameChange={handleFrameChange}
                    drawOverlay={drawOverlay}
                />
            )}

            {!segmentsData && !loading && !error && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                    Enter a schedule ID to view homography overlay
                </div>
            )}
        </div>
    );
};

export default HomographyViewer;
